import React from 'react';
import { useForm } from 'react-hook-form';
import './ClinicInfoText.css';
import {
    ClinicInfoPanel,
    VerificationBadge,
    ClinicStatusCard,
    ClinicLogoEditCard,
    VerificationSummaryCard,
    ClinicTimezoneCard,
    ClinicInfoFormField,
} from './clinicInfoParts';

const ClinicInfoForm = ({ clinicInfo, logoDimensions, onSubmit, getImageDimensions, isSaving = false }) => {
    const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm({
        defaultValues: clinicInfo || {},
    });

    const logoUrl = watch('logo');
    const isActive = clinicInfo?.status === 'ON';

    const formatPhoneNumber = (value) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 3) return numbers;
        if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
        return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    };

    const handlePhoneChange = (e) => {
        const formatted = formatPhoneNumber(e.target.value);
        setValue(e.target.name, formatted);
    };

    const handleLogoBlur = (event) => {
        const value = event.target.value?.trim();
        if (value && getImageDimensions) {
            getImageDimensions(value);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="clinic-info-view clinic-info-form">
            <div className="row g-3">
                <div className="col-lg-8">
                    <ClinicInfoPanel title="Basic Information" icon="bi-info-circle">
                        <div className="clinic-info-form-grid">
                            <ClinicInfoFormField
                                label="Clinic Name"
                                htmlFor="clinic-name"
                                error={errors.name?.message}
                            >
                                <input
                                    id="clinic-name"
                                    type="text"
                                    className={`form-control form-control-sm ${errors.name ? 'is-invalid' : ''}`}
                                    {...register('name', { required: 'Clinic name is required' })}
                                />
                            </ClinicInfoFormField>

                            <ClinicInfoFormField label="Clinic Number" htmlFor="clinic-number">
                                <input
                                    id="clinic-number"
                                    type="text"
                                    className="form-control form-control-sm"
                                    disabled
                                    {...register('number')}
                                />
                            </ClinicInfoFormField>

                            <ClinicInfoFormField label="Clinic Type" htmlFor="clinic-type">
                                <input
                                    id="clinic-type"
                                    type="text"
                                    className="form-control form-control-sm"
                                    disabled
                                    {...register('clinicType')}
                                />
                            </ClinicInfoFormField>

                            <ClinicInfoFormField label="Date Started" htmlFor="clinic-started">
                                <input
                                    id="clinic-started"
                                    type="text"
                                    className="form-control form-control-sm"
                                    disabled
                                    {...register('dateStarted')}
                                />
                            </ClinicInfoFormField>

                            <ClinicInfoFormField
                                label="Clinic Signature"
                                htmlFor="clinic-signature"
                                className="clinic-info-form-field--full"
                            >
                                <input
                                    id="clinic-signature"
                                    type="text"
                                    className="form-control form-control-sm"
                                    {...register('signature')}
                                />
                            </ClinicInfoFormField>
                        </div>
                    </ClinicInfoPanel>
                </div>

                <div className="col-lg-4">
                    <div className="clinic-info-aside">
                        <ClinicStatusCard isActive={isActive} />
                        <ClinicLogoEditCard
                            logo={logoUrl || clinicInfo?.logo}
                            logoDimensions={logoDimensions}
                            register={register}
                            onLogoBlur={handleLogoBlur}
                        />
                    </div>
                </div>
            </div>

            <div className="row g-3">
                <div className="col-lg-8">
                    <ClinicInfoPanel title="Contact Information" icon="bi-telephone">
                        <div className="clinic-info-form-grid">
                            <ClinicInfoFormField
                                label="Email"
                                htmlFor="clinic-email"
                                error={errors.email?.message}
                            >
                                <div className="clinic-info-form-field__inline">
                                    <input
                                        id="clinic-email"
                                        type="email"
                                        className={`form-control form-control-sm ${errors.email ? 'is-invalid' : ''}`}
                                        {...register('email', {
                                            required: 'Email is required',
                                            pattern: {
                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                message: 'Invalid email address',
                                            },
                                        })}
                                    />
                                    <VerificationBadge
                                        isVerified={clinicInfo?.verification?.email}
                                        fieldName="Email"
                                    />
                                </div>
                            </ClinicInfoFormField>

                            <ClinicInfoFormField
                                label="Phone"
                                htmlFor="clinic-phone"
                                error={errors.phone?.message}
                            >
                                <div className="clinic-info-form-field__inline">
                                    <input
                                        id="clinic-phone"
                                        type="tel"
                                        className={`form-control form-control-sm ${errors.phone ? 'is-invalid' : ''}`}
                                        {...register('phone', {
                                            required: 'Phone is required',
                                            validate: (value) =>
                                                value.replace(/\D/g, '').length === 10 || 'Must be 10 digits',
                                        })}
                                        onChange={handlePhoneChange}
                                        placeholder="(###) ###-####"
                                    />
                                    <VerificationBadge
                                        isVerified={clinicInfo?.verification?.phone}
                                        fieldName="Phone"
                                    />
                                </div>
                            </ClinicInfoFormField>

                            <ClinicInfoFormField
                                label="SMS"
                                htmlFor="clinic-sms"
                                error={errors.sms?.message}
                                hint="SMS number is managed by your clinic administrator."
                            >
                                <div className="clinic-info-form-field__inline">
                                    <input
                                        id="clinic-sms"
                                        type="tel"
                                        className="form-control form-control-sm"
                                        disabled
                                        {...register('sms')}
                                        placeholder="(###) ###-####"
                                    />
                                    <VerificationBadge
                                        isVerified={clinicInfo?.verification?.sms}
                                        fieldName="SMS"
                                    />
                                </div>
                            </ClinicInfoFormField>

                            <ClinicInfoFormField label="Website" htmlFor="clinic-website">
                                <input
                                    id="clinic-website"
                                    type="url"
                                    className="form-control form-control-sm"
                                    {...register('website')}
                                    placeholder="https://"
                                />
                            </ClinicInfoFormField>
                        </div>
                    </ClinicInfoPanel>
                </div>

                <div className="col-lg-4">
                    <VerificationSummaryCard verification={clinicInfo?.verification} />
                </div>
            </div>

            <div className="row g-3">
                <div className="col-lg-8">
                    <ClinicInfoPanel title="Address" icon="bi-geo-alt">
                        <div className="clinic-info-form-grid clinic-info-form-grid--address">
                            <ClinicInfoFormField label="Street" htmlFor="clinic-street" className="clinic-info-form-field--full">
                                <input
                                    id="clinic-street"
                                    type="text"
                                    className="form-control form-control-sm"
                                    {...register('street')}
                                    placeholder="Street address"
                                />
                            </ClinicInfoFormField>

                            <ClinicInfoFormField label="City" htmlFor="clinic-city">
                                <input
                                    id="clinic-city"
                                    type="text"
                                    className="form-control form-control-sm"
                                    {...register('city')}
                                    placeholder="City"
                                />
                            </ClinicInfoFormField>

                            <ClinicInfoFormField label="Province" htmlFor="clinic-province">
                                <input
                                    id="clinic-province"
                                    type="text"
                                    className="form-control form-control-sm"
                                    {...register('province')}
                                    placeholder="Province"
                                />
                            </ClinicInfoFormField>

                            <ClinicInfoFormField label="Postal Code" htmlFor="clinic-postal">
                                <input
                                    id="clinic-postal"
                                    type="text"
                                    className="form-control form-control-sm"
                                    {...register('postalCode')}
                                    placeholder="Postal code"
                                />
                            </ClinicInfoFormField>

                            <ClinicInfoFormField label="Country" htmlFor="clinic-country">
                                <input
                                    id="clinic-country"
                                    type="text"
                                    className="form-control form-control-sm"
                                    {...register('country')}
                                    placeholder="Country"
                                />
                            </ClinicInfoFormField>
                        </div>
                    </ClinicInfoPanel>
                </div>

                <div className="col-lg-4">
                    <ClinicTimezoneCard timezoneName={clinicInfo?.timezone?.timezoneName} />
                </div>
            </div>

            <div className="clinic-info-form__footer">
                <button
                    type="submit"
                    className="btn btn-sm btn-light-primary rounded-pill px-4"
                    disabled={isSaving}
                >
                    <i className={`bi ${isSaving ? 'bi-arrow-clockwise ecx-refresh-spin' : 'bi-check-lg'} me-1`}></i>
                    {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
            </div>
        </form>
    );
};

export default ClinicInfoForm;
